/**
 * ClaudeAssistantPage — AI-powered chat assistant for pharmacy staff.
 * Supports multiple models (Sonnet, Opus, Haiku, Fable) and purposes
 * (general chat, drug interaction, report summary).
 * Streams responses via SSE for real-time token display.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChatMarkdown } from '../components/ChatMarkdown';
import { ExpiryQuickActions } from '../components/duti/ExpiryQuickActions';
import { ChatRetryNotice, chatFailure, chatHttpError } from '../components/duti/ChatRetryNotice';
import {
  Bot, Send, Loader2, Sparkles, Trash2, Plus, User, X,
} from 'lucide-react';
import api, { authFetch } from '../lib/api';
import { DUTI_AI_AVATAR } from '../lib/brand';

const MODELS = [
  { id: 'sonnet', label: 'Sonnet 5.5', desc: 'Balanced' },
  { id: 'opus', label: 'Opus 5.5', desc: 'Deep reasoning' },
  { id: 'haiku', label: 'Haiku 4.5', desc: 'Fast' },
  { id: 'fable', label: 'Fable 5.1', desc: 'Advanced' },
];

const PURPOSES = [
  { id: 'staff_assistant', label: 'General Assistant' },
  { id: 'drug_interaction', label: 'Drug Interaction' },
  { id: 'report_summary', label: 'Report Summary' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClaudeAssistantPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [model, setModel] = useState('sonnet');
  const [purpose, setPurpose] = useState('staff_assistant');
  const [sessionId, setSessionId] = useState(null);
  const [contextSubject, setContextSubject] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [failure, setFailure] = useState(null);
  const [sessions, setSessions] = useState([]);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => bottomRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Load session list
  const loadSessions = useCallback(() => {
    api.get('/claude/sessions').then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const loadSession = async (sid) => {
    try {
      const { data } = await api.get(`/claude/sessions/${sid}`);
      setMessages(data.messages || []);
      setSessionId(sid);
      setContextSubject('');
      setFailure(null);
    } catch { toast.error('Could not load session'); }
  };

  const newChat = () => {
    setFailure(null);
    setMessages([]);
    setSessionId(null);
    setInput('');
    setContextSubject('');
  };

  const deleteSession = async (sid) => {
    try {
      await api.delete(`/claude/sessions/${sid}`);
      if (sessionId === sid) newChat();
      loadSessions();
    } catch { toast.error('Delete failed'); }
  };

  const sendMessage = async (resend) => {
    const text = (typeof resend === 'string' ? resend : input).trim();
    if (!text || streaming) return;
    const dropFailed = !!failure;
    setFailure(null);

    const userMsg = { role: 'user', content: text, ts: new Date().toISOString() };
    setMessages((prev) => [...(dropFailed ? prev.slice(0, -1) : prev), userMsg]);
    setInput('');
    setStreaming(true);

    // Add a placeholder for the assistant reply
    const assistantIdx = messages.length - (dropFailed ? 1 : 0) + 1;
    setMessages((prev) => [...prev, { role: 'assistant', content: '', ts: new Date().toISOString() }]);

    try {
      const res = await authFetch(`${API_URL}/api/claude/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, session_id: sessionId, model, purpose }),
      });

      if (!res.ok) throw await chatHttpError(res);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let full = '';
      let streamError = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'session' && evt.session_id) {
              setSessionId(evt.session_id);
            } else if (evt.type === 'delta') {
              full += evt.content;
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === 'assistant') {
                  updated[updated.length - 1] = { ...last, content: last.content + evt.content };
                }
                return updated;
              });
            } else if (evt.type === 'expiry_batches') {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === 'assistant') {
                  updated[updated.length - 1] = { ...last, expiryBatches: evt.batches };
                }
                return updated;
              });
            } else if (evt.type === 'chat_context') {
              setContextSubject(evt.subject || '');
            } else if (evt.type === 'error') {
              streamError = evt.message || 'Duti AI couldn\'t answer just now.';
            }
          } catch { /* skip malformed lines */ }
        }
      }
      if (streamError && !full) throw Object.assign(new Error(streamError), { status: 500 });
      loadSessions();
    } catch (e) {
      setFailure(chatFailure(e, text));
      // Remove the empty assistant placeholder
      setMessages((prev) => prev.filter((_, i) => i !== assistantIdx));
    } finally {
      setStreaming(false);
    }
  };

  const retry = (text) => sendMessage(text);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)] max-w-6xl" data-testid="claude-assistant-page">
      {/* Sidebar — sessions */}
      <aside className="w-56 shrink-0 border-r flex flex-col bg-muted/30 hidden lg:flex">
        <div className="p-3 border-b">
          <Button variant="outline" className="w-full gap-1.5 text-xs" onClick={newChat} data-testid="claude-new-chat">
            <Plus className="h-3.5 w-3.5" /> New Chat
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.map((s) => (
            <div
              key={s.session_id}
              className={`group flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs cursor-pointer transition-colors ${
                s.session_id === sessionId ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'
              }`}
              onClick={() => loadSession(s.session_id)}
              data-testid={`claude-session-${s.session_id}`}
            >
              <Bot className="h-3 w-3 shrink-0" />
              <span className="truncate flex-1">{s.purpose || 'Chat'}</span>
              <button
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-500"
                onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b bg-background/80 backdrop-blur-sm">
          <div className="flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Duti AI</span>
          </div>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger className="w-36 h-7 text-xs" data-testid="claude-model-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODELS.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.label} <span className="text-muted-foreground ml-1">· {m.desc}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={purpose} onValueChange={setPurpose}>
            <SelectTrigger className="w-40 h-7 text-xs" data-testid="claude-purpose-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PURPOSES.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" className="lg:hidden ml-auto text-xs" onClick={newChat}>
            <Plus className="h-3.5 w-3.5 mr-1" /> New
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground" data-testid="claude-empty-state">
              <Sparkles className="h-10 w-10 mb-3 opacity-30" />
              <p className="text-sm font-medium">How can I help you today?</p>
              <p className="text-xs mt-1 max-w-xs">
                Ask about drug interactions, dosage calculations, inventory tips, or anything pharmacy-related.
              </p>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`} data-testid={`claude-msg-${i}`}>
              {msg.role === 'assistant' && (
                <img src={DUTI_AI_AVATAR} alt="Duti AI" className="w-7 h-7 rounded-full shrink-0 mt-0.5" data-testid="claude-assistant-avatar" />
              )}
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted'
              }`}>
                {msg.role === 'assistant' && msg.content ? (
                  <ChatMarkdown content={msg.content} />
                ) : (
                  <div className="whitespace-pre-wrap">{msg.content || (streaming && i === messages.length - 1 ? '...' : '')}</div>
                )}
                {msg.expiryBatches?.length > 0 && <ExpiryQuickActions batches={msg.expiryBatches} />}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-foreground/10 flex items-center justify-center shrink-0 mt-0.5">
                  <User className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
          {failure && !streaming && (
            <div className="max-w-3xl">
              <ChatRetryNotice key={`${failure.text}-${failure.message}`} failure={failure} onRetry={retry} testId="claude-retry" />
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="border-t p-3 bg-background">
          {contextSubject && (
            <div className="max-w-3xl mx-auto mb-2 flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="claude-context-chip">
              <span className="inline-flex items-center gap-1 rounded-full border bg-muted/60 px-2 py-0.5">
                Continuing about <span className="font-medium text-foreground">{contextSubject}</span>
                <button type="button" onClick={newChat} className="hover:text-foreground" aria-label="Clear conversation context"
                  data-testid="claude-context-clear">
                  <X className="h-3 w-3" />
                </button>
              </span>
            </div>
          )}
          <div className="flex items-end gap-2 max-w-3xl mx-auto">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message... (Enter to send, Shift+Enter for new line)"
              className="min-h-[44px] max-h-32 resize-none text-sm"
              disabled={streaming}
              data-testid="claude-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!input.trim() || streaming}
              className="h-11 w-11 shrink-0"
              data-testid="claude-send"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground text-center mt-1.5">
            Claude AI may make mistakes. Verify important clinical information.
          </p>
        </div>
      </div>
    </div>
  );
}
